var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PlanTypeCategory, Role } from '../../enum';
import { useUserSessionAPI } from '../app/selectors';
import { FallbackComponent } from '../common';
import { useLockPlanAPI, useSeatCapacityCheckAPI, useUnlockPlanAPI, } from '../home/selectors';
import { useDialog } from '../hooks';
import { useReportAPI } from '../report/selectors';
import { isSeatCapacityAlertsEnabled } from '../seatCapacity';
import { useIsCompletedSimulation } from '../toastify/selector';
import { WizardStep } from '../wizard/constants';
import { FinancialTable, ResourcingTable } from './annualTable';
import { DeleteScenarioDialog } from './deleteDialog';
import { EditScenarioDialog } from './editDialog';
import { HeadcountChangeDialog } from './headcountChangeDialog';
import { LockPlanDialog } from './lockPlanDialog';
import { MultiskillDeleteDialog } from './multiskillDeleteDialog';
import { MultiskillDialog } from './multiskillDialog';
import { ProdHoursResourcingTable } from './prodHoursResourcingTable';
import { RenameScenarioDialog } from './renameDialog';
import { SeatCapacityWarningDialog } from './seatCapacityWarningDialog';
import { useSummaryReportAPI } from './selectors';
import { SwitchScenarioDialog } from './switchDialog';
import { DialogMode } from './types';
var isSuccessStatus = function (statusCode) { return !!statusCode && statusCode >= 200 && statusCode < 300; };
var AnnualReport = function (props) {
    var _a = useReportAPI(), reportData = _a[0], fetchReportData = _a[1];
    var basePlanId = useParams().basePlanId;
    var _b = useSummaryReportAPI(), summaryReportData = _b[0], fetchSummaryReport = _b[1];
    var isCompletedSimulation = useIsCompletedSimulation(basePlanId);
    var needFetchData = useRef(true);
    var fetchReportDataRef = useRef(fetchReportData);
    fetchReportDataRef.current = fetchReportData;
    var fetchSummaryReportRef = useRef(fetchSummaryReport);
    fetchSummaryReportRef.current = fetchSummaryReport;
    useEffect(function () {
        needFetchData.current = true;
    }, [basePlanId]);
    useEffect(function () {
        if (needFetchData.current || isCompletedSimulation) {
            needFetchData.current = false;
            fetchSummaryReportRef.current(isCompletedSimulation);
            fetchReportDataRef.current();
        }
    }, [basePlanId, isCompletedSimulation]);
    return (_jsx(FallbackComponent, __assign({ requesting: !!summaryReportData.requesting || !!reportData.requesting }, { children: props.children })));
};
export var AnnualResourcingReport = function () {
    var _a;
    var _b = useReportAPI(), reportData = _b[0], fetchReportData = _b[1];
    var _c = useSummaryReportAPI(), fetchSummaryReport = _c[1];
    var userSession = useUserSessionAPI()[0].data;
    var _d = useDialog(), dialogMode = _d.dialogMode, selectedScenario = _d.data, handleOpenDialog = _d.handleOpenDialog, handleCloseDialog = _d.handleCloseDialog;
    var _e = useLockPlanAPI(selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id), lockPlanData = _e[0], lockPlan = _e[1], clearLockPlan = _e[2];
    var _f = useUnlockPlanAPI(selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id), unlockPlanData = _f[0], unlockPlan = _f[1], clearUnlockPlan = _f[2];
    // when the lock/unlock API call succeeds, refresh the report data and clear the lock/unlock state
    useEffect(function () {
        if (isSuccessStatus(lockPlanData.statusCode)) {
            // force: the summary is cached (useCachedAPI) and won't refetch on its own,
            // but locking changed the backend data, so bypass the cache here.
            fetchSummaryReport(true);
            fetchReportData();
            clearLockPlan();
        }
    }, [lockPlanData.statusCode, fetchSummaryReport, fetchReportData, clearLockPlan]);
    useEffect(function () {
        if (isSuccessStatus(unlockPlanData.statusCode)) {
            fetchSummaryReport(true);
            fetchReportData();
            clearUnlockPlan();
        }
    }, [unlockPlanData.statusCode, fetchSummaryReport, fetchReportData, clearUnlockPlan]);
    var _g = useSeatCapacityCheckAPI(selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id), seatCheck = _g[0], fetchSeatCheck = _g[1], clearSeatCheck = _g[2];
    var _h = useState(null), seatWarningLocations = _h[0], setSeatWarningLocations = _h[1];
    // After the user confirms Lock, we always re-fetch seat capacity and await that result
    // so a cleared prior response never locks without the advisory dialog.
    var _j = useState(false), awaitingSeatCheck = _j[0], setAwaitingSeatCheck = _j[1];
    var canUnlockSelectedScenario = !(selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.isLocked) ||
        (userSession === null || userSession === void 0 ? void 0 : userSession.versionId) === Role.Admin ||
        (userSession === null || userSession === void 0 ? void 0 : userSession.versionId) === Role.Superadmin ||
        (!!(userSession === null || userSession === void 0 ? void 0 : userSession.id) && userSession.id === (selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.lockedBy));
    // Warm the seat-capacity check when the lock dialog opens (optimistic UX); confirm still re-fetches.
    useEffect(function () {
        if (isSeatCapacityAlertsEnabled() &&
            dialogMode === DialogMode.LockPlan &&
            (selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id) &&
            !selectedScenario.isLocked) {
            fetchSeatCheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogMode, selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id, selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.isLocked]);
    // When awaiting a seat check for lock, proceed only after the request settles with a status.
    useEffect(function () {
        var _a;
        if (!awaitingSeatCheck)
            return;
        if (seatCheck.requesting)
            return;
        if (seatCheck.statusCode === undefined)
            return;
        setAwaitingSeatCheck(false);
        handleCloseDialog();
        if (isSuccessStatus(seatCheck.statusCode) && ((_a = seatCheck.data) === null || _a === void 0 ? void 0 : _a.hasWarnings)) {
            setSeatWarningLocations(seatCheck.data.exceeded);
            return;
        }
        // No warnings, request failed, or empty body: lock straight away (check is advisory).
        lockPlan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awaitingSeatCheck, seatCheck.requesting, seatCheck.statusCode, seatCheck.data]);
    var handleConfirmLockPlan = function () {
        if (!(selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id))
            return;
        if (selectedScenario.isLocked) {
            if (!canUnlockSelectedScenario)
                return;
            unlockPlan();
            handleCloseDialog();
            return;
        }
        if (!isSeatCapacityAlertsEnabled()) {
            // Seat-capacity alerts are disabled: skip the advisory check entirely and lock straight away.
            lockPlan();
            handleCloseDialog();
            return;
        }
        // Always re-fetch: never treat missing/cleared data as "no warnings".
        clearSeatCheck();
        setAwaitingSeatCheck(true);
        fetchSeatCheck();
    };
    var handleConfirmSeatWarning = function () {
        setSeatWarningLocations(null);
        clearSeatCheck();
        lockPlan();
    };
    var handleCloseSeatWarning = function () {
        setSeatWarningLocations(null);
        clearSeatCheck();
    };
    // Determine if this is a production hours plan
    var isProdHoursPlan = ((_a = reportData.data) === null || _a === void 0 ? void 0 : _a.planTypeCategory) === PlanTypeCategory.ProdHours;
    var resourcingContent = isProdHoursPlan ? (_jsx(ProdHoursResourcingTable, { handleOpenDialog: handleOpenDialog })) : (_jsx(ResourcingTable, { handleOpenDialog: handleOpenDialog }));
    return (_jsxs(AnnualReport, { children: [resourcingContent, _jsx(DeleteScenarioDialog, { open: dialogMode === DialogMode.DeleteScenario, scenario: selectedScenario, onClose: handleCloseDialog }), _jsx(RenameScenarioDialog, { type: dialogMode, open: dialogMode === DialogMode.RenameScenario || dialogMode === DialogMode.RenameAfterSwitch, scenario: selectedScenario, onClose: handleCloseDialog }), _jsx(SwitchScenarioDialog, { handleOpenDialog: handleOpenDialog, open: dialogMode === DialogMode.SwitchScenario, scenario: selectedScenario, onClose: handleCloseDialog }), _jsx(MultiskillDialog, { open: dialogMode === DialogMode.Multiskill, onClose: handleCloseDialog, scenario: selectedScenario }), _jsx(MultiskillDeleteDialog, { open: dialogMode === DialogMode.MultiskillDelete, scenario: selectedScenario, onClose: handleCloseDialog }), _jsx(HeadcountChangeDialog, { open: dialogMode === DialogMode.HeadcountChange, scenarioId: selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id, onClose: handleCloseDialog }), _jsx(EditScenarioDialog, { open: dialogMode === DialogMode.DeleteAllMultiskill, scenario: selectedScenario, to: "/wizard/".concat(selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.basePlanId, "/").concat(selectedScenario === null || selectedScenario === void 0 ? void 0 : selectedScenario.id, "?step=").concat(WizardStep.Review), onClose: handleCloseDialog }), _jsx(LockPlanDialog, { open: dialogMode === DialogMode.LockPlan, scenario: selectedScenario, canUnlock: canUnlockSelectedScenario, loading: awaitingSeatCheck || !!seatCheck.requesting, onClose: handleCloseDialog, onConfirm: handleConfirmLockPlan }), _jsx(SeatCapacityWarningDialog, { open: !!seatWarningLocations, locations: seatWarningLocations !== null && seatWarningLocations !== void 0 ? seatWarningLocations : [], onClose: handleCloseSeatWarning, onConfirm: handleConfirmSeatWarning })] }));
};
export var AnnualFinancialReport = function () {
    var _a = useDialog(), dialogMode = _a.dialogMode, selectedScenario = _a.data, handleOpenDialog = _a.handleOpenDialog, handleCloseDialog = _a.handleCloseDialog;
    return (_jsxs(AnnualReport, { children: [_jsx(FinancialTable, { handleOpenDialog: handleOpenDialog }), _jsx(RenameScenarioDialog, { type: DialogMode.RenameFinancial, open: dialogMode === DialogMode.RenameScenario, scenario: selectedScenario, onClose: handleCloseDialog })] }));
};
